import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { useSignalsTriage, type SignalsTriageFilters } from '../../hooks/useAdmin';
import {
  type CohortItem,
  type HealthIssue,
  type IssueAction,
  type ResolveResult,
  type StepperStage,
  useCohort,
  useCandidateAggregatorRerun,
  useDealView,
  useHealthIssues,
  usePipelineEvents,
  useResolveQuery,
  useSignalView,
} from '../../hooks/usePipelineInsights';
import type { SignalTriageRow } from '../../types';
import type { ForwardTraceResponse } from '../../types/trace';
import { BackwardTracePane, ForwardTimeline } from './TracePage';
import {
  EMBER_NAV_EVENT,
  useUrlState,
  useViewParam,
} from '../../hooks/navigation/useUrlState';
import { deepLinks } from '../../utils/deepLinks';
import styles from './PipelineInsightsPage.module.css';

// PR 5 — Email Intake sub-mode. Preset filter chips on the Investigate
// search bar that switch the results pane to per-signal triage rows.
// Pre-filter tuning use case from the retired Signals Triage tab.
type IntakeChip =
  | 'rejected_before_classifier'
  | 'classified_non_origination'
  | 'newsletter'
  | 'portfolio_company';

interface IntakeChipDef {
  key: IntakeChip;
  label: string;
  filters: SignalsTriageFilters;
}

const INTAKE_CHIPS: IntakeChipDef[] = [
  {
    key: 'rejected_before_classifier',
    label: 'Rejected before classifier',
    filters: { status: 'auto_discarded' },
  },
  {
    key: 'classified_non_origination',
    label: 'Classified non-origination',
    filters: { status: 'discarded', discardReason: 'classifier_rejected' },
  },
  {
    key: 'newsletter',
    label: 'Newsletter survivors',
    filters: { status: 'auto_discarded', discardReason: 'newsletter' },
  },
  {
    key: 'portfolio_company',
    label: 'Portfolio company',
    filters: { status: 'auto_discarded', discardReason: 'portco_operations' },
  },
];

// Pipeline Insights — Investigate + Health modes. PR 1 scope: skeleton +
// Investigate (universal search → Deal or Signal object view with plain-
// English summary + pipeline stepper + timeline). Health mode stub for
// PR 2. Spec: docs/archive/pre-cut-2026-08/phase-b-review-surface-audit-spec.md.

type Mode = 'investigate' | 'health' | 'events';

interface ObjectSelection {
  kind: 'signal' | 'deal';
  signalId?: number;
  dealKind?: 'candidate' | 'affinity_opp';
  dealKey?: string;
}

function readUrlSelection(): ObjectSelection | null {
  if (typeof window === 'undefined') return null;
  const sp = new URLSearchParams(window.location.search);
  const sig = sp.get('signal');
  if (sig && /^\d+$/.test(sig)) {
    return { kind: 'signal', signalId: parseInt(sig, 10) };
  }
  const dk = sp.get('deal_kind');
  const key = sp.get('deal_key');
  if (dk && key && (dk === 'candidate' || dk === 'affinity_opp')) {
    return { kind: 'deal', dealKind: dk, dealKey: key };
  }
  return null;
}

/** Build the URL for a (compound) selection, preserving sibling params + hash. */
function selectionUrl(sel: ObjectSelection | null): string {
  const url = new URL(window.location.href);
  url.searchParams.delete('signal');
  url.searchParams.delete('deal_kind');
  url.searchParams.delete('deal_key');
  if (sel?.kind === 'signal' && sel.signalId !== undefined) {
    url.searchParams.set('signal', String(sel.signalId));
  } else if (sel?.kind === 'deal' && sel.dealKind && sel.dealKey) {
    url.searchParams.set('deal_kind', sel.dealKind);
    url.searchParams.set('deal_key', sel.dealKey);
  }
  return url.pathname + url.search + url.hash;
}

/**
 * Compound selection (signal OR deal_kind+deal_key) as a deep link, mirroring
 * useSelectionParam (spec §4.2) for the multi-param case. Drilling in from
 * search pushes (Back returns to the search); swapping between results replaces;
 * clearing back()s when we own the entry, else drops the params. The originating
 * `q`/`intake`/`mode` params are left untouched so Back restores the exact search
 * (spec §5.3).
 */
function useInsightsSelection(): {
  selection: ObjectSelection | null;
  setSelection: (sel: ObjectSelection | null) => void;
} {
  const [selection, setSel] = useState<ObjectSelection | null>(() => readUrlSelection());

  useEffect(() => {
    const resync = () => setSel(readUrlSelection());
    window.addEventListener('popstate', resync);
    window.addEventListener(EMBER_NAV_EVENT, resync);
    return () => {
      window.removeEventListener('popstate', resync);
      window.removeEventListener(EMBER_NAV_EVENT, resync);
    };
  }, []);

  const setSelection = useCallback((sel: ObjectSelection | null) => {
    if (sel === null) {
      // Clearing must be IDEMPOTENT: typing in the search box calls this on
      // every keystroke while a deal is open. A `history.back()` here would
      // re-fire on each keystroke (React `selection` lags the async pop),
      // popping multiple entries and yanking the user off the page (review F3).
      // Drop the params in place via replace; browser Back still returns to the
      // pre-selection search via the push at drill-in below.
      if (readUrlSelection() === null) {
        setSel(null);
        return;
      }
      window.history.replaceState(window.history.state, '', selectionUrl(null));
      window.dispatchEvent(new CustomEvent(EMBER_NAV_EVENT));
      setSel(null);
      return;
    }
    const next = selectionUrl(sel);
    if (readUrlSelection() === null) {
      // Drill in from search → push so browser Back returns to the search.
      window.history.pushState({ emberSel: 'insights' }, '', next);
    } else {
      // Swapping between sibling results — replace, don't stack history.
      window.history.replaceState(window.history.state, '', next);
    }
    window.dispatchEvent(new CustomEvent(EMBER_NAV_EVENT));
    setSel(sel);
  }, []);

  return { selection, setSelection };
}

// PR 6 — Deal-card "Show similar overrides" deep-link. Reads
// ?mode=health&cohort=<>&field_name=<> on mount and auto-opens the
// cohort modal in Health mode.
interface InitialCohort {
  cohort: string;
  fieldName?: string;
  days?: number;
}

function readInitialCohort(): InitialCohort | null {
  if (typeof window === 'undefined') return null;
  const sp = new URLSearchParams(window.location.search);
  if (sp.get('mode') !== 'health') return null;
  const cohort = sp.get('cohort');
  if (!cohort) return null;
  return {
    cohort,
    fieldName: sp.get('field_name') || undefined,
    days: sp.get('days') ? parseInt(sp.get('days')!, 10) : undefined,
  };
}

export function PipelineInsightsPage() {
  // `mode` is a which-list sub-view → push (Back-able), written on change and
  // 3-way (investigate/health/events) so a deep-linked or in-app mode persists
  // across reloads and tab round-trips (spec §5.3). `events` is the default
  // landing view, so it drops the param; investigate/health are explicit.
  // NOTE: a signal/deal selection only renders in Investigate, so all
  // selection deep links MUST carry `mode=investigate` (deepLinks.ts +
  // observability.py). A bare `?deal_key=…`/`?signal=…` would land on Events.
  const [mode, setMode] = useUrlState<Mode>('mode', {
    parse: (raw) => (raw === 'investigate' || raw === 'health' ? raw : 'events'),
    serialize: (v) => (v === 'events' ? null : v),
    history: 'push',
  });
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  // Compound selection (signal/deal) as a push deep link; the wrapper keeps the
  // existing setSelection(sel|null) call sites unchanged.
  const { selection, setSelection } = useInsightsSelection();

  // Debounce the query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  const resolveQuery = useResolveQuery(debouncedQuery, selection === null);

  // Auto-resolve when the query returns a single hit
  useEffect(() => {
    if (resolveQuery.data && selection === null) {
      const r: ResolveResult = resolveQuery.data;
      if (r.kind === 'signal') {
        setSelection({ kind: 'signal', signalId: r.signal_id });
      } else if (r.kind === 'deal') {
        setSelection({
          kind: 'deal', dealKind: r.deal_kind, dealKey: r.deal_key,
        });
      }
    }
  }, [resolveQuery.data, selection]);

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <h1 className={styles.title}>Pipeline Insights</h1>
        <div className={styles.segmented}>
          <button
            type="button"
            className={`${styles.segBtn} ${mode === 'investigate' ? styles.segBtnActive : ''}`}
            onClick={() => setMode('investigate')}
          >
            Investigate
          </button>
          <button
            type="button"
            className={`${styles.segBtn} ${mode === 'health' ? styles.segBtnActive : ''}`}
            onClick={() => setMode('health')}
          >
            Health
          </button>
          <button
            type="button"
            className={`${styles.segBtn} ${mode === 'events' ? styles.segBtnActive : ''}`}
            onClick={() => setMode('events')}
          >
            Events
          </button>
        </div>
      </header>

      {mode === 'investigate' && (
        <InvestigateMode
          query={query}
          setQuery={setQuery}
          debouncedQuery={debouncedQuery}
          selection={selection}
          setSelection={setSelection}
          resolveQuery={resolveQuery}
        />
      )}

      {mode === 'health' && (
        <HealthMode
          onJumpToSignal={(id) => {
            setMode('investigate');
            setSelection({ kind: 'signal', signalId: id });
          }}
          onJumpToDeal={(k, key) => {
            setMode('investigate');
            setSelection({ kind: 'deal', dealKind: k, dealKey: key });
          }}
        />
      )}

      {mode === 'events' && (
        <EventsMode
          onJumpToSignal={(id) => {
            setMode('investigate');
            setSelection({ kind: 'signal', signalId: id });
          }}
        />
      )}
    </div>
  );
}

interface InvestigateModeProps {
  query: string;
  setQuery: (q: string) => void;
  debouncedQuery: string;
  selection: ObjectSelection | null;
  setSelection: (s: ObjectSelection | null) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolveQuery: any;
}

function InvestigateMode({
  query, setQuery, debouncedQuery, selection, setSelection, resolveQuery,
}: InvestigateModeProps) {
  // PR 5 — Email Intake sub-mode. Mutually exclusive with free-text
  // search: activating a chip clears the query; typing in the box
  // clears the chip. Lives in the URL (replace — reload-stable + shareable).
  const [intakeParam, setIntakeParam] = useViewParam('intake', '', { history: 'replace' });
  const intakeChip = (intakeParam || null) as IntakeChip | null;
  const setIntakeChip = (c: IntakeChip | null) => setIntakeParam(c ?? '');

  return (
    <div className={styles.investigate}>
      <div className={styles.searchBar}>
        <input
          type="text"
          autoFocus
          className={styles.searchInput}
          placeholder="Search by deal name, codename, signal id, opp id, subject, sender…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelection(null);
            if (e.target.value) setIntakeChip(null);
          }}
        />
        {(selection !== null || intakeChip !== null) && (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={() => {
              setQuery('');
              setSelection(null);
              setIntakeChip(null);
            }}
          >
            ← Back to search
          </button>
        )}
      </div>

      {/* Email Intake preset chips — hidden once an object is opened. */}
      {selection === null && (
        <div className={styles.chipRow}>
          <span className={styles.chipRowLabel}>Email Intake:</span>
          {INTAKE_CHIPS.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`${styles.chip} ${intakeChip === c.key ? styles.chipActive : ''}`}
              onClick={() => {
                setIntakeChip(intakeChip === c.key ? null : c.key);
                setQuery('');
              }}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      {/* Triage-row results when a chip is active */}
      {selection === null && intakeChip !== null && (
        <TriageResults
          chipKey={intakeChip}
          onPick={(signalId) => {
            setSelection({ kind: 'signal', signalId });
          }}
        />
      )}

      {/* Search results list when multiple hits */}
      {selection === null && intakeChip === null && debouncedQuery.trim().length > 0 && (
        <SearchResults
          loading={resolveQuery.isLoading}
          data={resolveQuery.data}
          onPick={(sel) => setSelection(sel)}
        />
      )}

      {/* Object view when resolved */}
      {selection?.kind === 'signal' && selection.signalId !== undefined && (
        <SignalView signalId={selection.signalId} />
      )}
      {selection?.kind === 'deal' && selection.dealKind && selection.dealKey && (
        <DealView dealKind={selection.dealKind} dealKey={selection.dealKey} />
      )}

      {selection === null && intakeChip === null && debouncedQuery.trim().length === 0 && (
        <div className={styles.empty}>
          Type to search. Single hit auto-resolves; multiple hits show a
          picker. Or click an Email Intake chip to triage pre-filter survivors.
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// PR 5 — Triage-row results when an Email Intake chip is active.
// Re-uses the /api/admin/signals-triage endpoint; row click hands off
// into the Signal object view (universal-search resolution flow).
// ===========================================================================

interface TriageResultsProps {
  chipKey: IntakeChip;
  onPick: (signalId: number) => void;
}

function TriageResults({ chipKey, onPick }: TriageResultsProps) {
  const chipDef = INTAKE_CHIPS.find((c) => c.key === chipKey)!;
  const q = useSignalsTriage({ ...chipDef.filters, sinceDays: 14, limit: 50 });

  if (q.isLoading) return <div className={styles.empty}>Loading…</div>;
  if (q.error) {
    return (
      <div className={styles.errorBox}>
        Failed: {(q.error as Error).message}
      </div>
    );
  }
  const rows = q.data?.rows ?? [];
  if (rows.length === 0) {
    return (
      <div className={styles.empty}>
        No signals in the last 14 days match this filter.
      </div>
    );
  }
  return (
    <ul className={styles.triageList}>
      {rows.map((r: SignalTriageRow) => (
        <li key={r.signal_id}>
          <button
            type="button"
            className={styles.triageRow}
            onClick={() => onPick(r.signal_id)}
          >
            <div className={styles.triageRowTop}>
              <span className={styles.triageSubject}>
                {r.subject || '(no subject)'}
              </span>
              <span className={styles.triageVerdict}>
                {r.status === 'auto_discarded' && r.discard_reason
                  ? `auto: ${r.discard_reason}`
                  : r.status === 'discarded'
                    ? `discarded: ${r.discard_reason ?? 'unknown'}`
                    : r.status}
              </span>
            </div>
            <div className={styles.triageRowMeta}>
              <span>{r.sender ?? '(no sender)'}</span>
              <span className={styles.triageDot}>·</span>
              <span>
                {r.received_at
                  ? new Date(r.received_at).toLocaleDateString()
                  : '—'}
              </span>
              {r.pre_filter_reasons && r.pre_filter_reasons.length > 0 && (
                <>
                  <span className={styles.triageDot}>·</span>
                  <span className={styles.triageReasons}>
                    {r.pre_filter_reasons.join(', ')}
                  </span>
                </>
              )}
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

function SearchResults({
  loading, data, onPick,
}: {
  loading: boolean;
  data: ResolveResult | undefined;
  onPick: (s: ObjectSelection) => void;
}) {
  if (loading) return <div className={styles.empty}>Searching…</div>;
  if (!data) return null;
  if (data.kind !== 'list') return null;
  if (data.results.length === 0) {
    return <div className={styles.empty}>(no matches)</div>;
  }
  return (
    <ul className={styles.resultList}>
      {data.results.map((h) => (
        <li key={`${h.kind}-${h.id}`}>
          <button
            type="button"
            className={styles.resultRow}
            onClick={() => {
              if (h.kind === 'signal') {
                onPick({ kind: 'signal', signalId: Number(h.id) });
              } else if (h.kind === 'candidate' || h.kind === 'affinity_opp') {
                onPick({
                  kind: 'deal',
                  dealKind: h.kind,
                  dealKey: String(h.id),
                });
              }
            }}
          >
            <span className={styles.resultKind}>{h.kind}</span>
            <span className={styles.resultLabel}>{h.label || String(h.id)}</span>
            {h.sublabel && (
              <span className={styles.resultSublabel}>{h.sublabel}</span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}

function SignalView({ signalId }: { signalId: number }) {
  const q = useSignalView(signalId);
  if (q.isLoading) return <div className={styles.empty}>Loading…</div>;
  if (q.error) {
    return (
      <div className={styles.errorBox}>
        Failed: {(q.error as Error).message}
      </div>
    );
  }
  if (!q.data) return null;
  return (
    <ObjectView
      view={q.data.view}
      timelineLabel={`Signal #${signalId}`}
      timeline={
        <ForwardTimeline
          data={q.data.timeline as ForwardTraceResponse}
          compact={false}
        />
      }
    />
  );
}

function DealView({
  dealKind, dealKey,
}: {
  dealKind: 'candidate' | 'affinity_opp';
  dealKey: string;
}) {
  const q = useDealView(dealKind, dealKey);
  if (q.isLoading) return <div className={styles.empty}>Loading…</div>;
  if (q.error) {
    return (
      <div className={styles.errorBox}>
        Failed: {(q.error as Error).message}
      </div>
    );
  }
  if (!q.data) return null;
  // BackwardTracePane refetches via its own hook; tanstack-query
  // dedupes the request against any sibling caller. The 3-column
  // layout (deal context · events · selected-event drilldown) is
  // identical to the retired Trace tab — no behavioral regression
  // from the operator's POV.
  return (
    <ObjectView
      view={q.data.view}
      timelineLabel={`${dealKind === 'candidate' ? 'Candidate' : 'Affinity opp'} ${dealKey}`}
      titleAction={
        <div className={styles.objectActionGroup}>
          {dealKind === 'candidate' && (
            <CandidateRerunButton dealKey={dealKey} />
          )}
          <a
            href={deepLinks.deal(dealKey)}
            className={styles.openInDealsChip}
            title="Open this deal in the Deals tab"
          >
            Open in Deals →
          </a>
        </div>
      }
      timeline={<BackwardTracePane dealKind={dealKind} dealKey={dealKey} />}
    />
  );
}

export function CandidateRerunButton({ dealKey }: { dealKey: string }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const rerun = useCandidateAggregatorRerun();

  // Queue-only Phase B — the rerun is enqueued, not run inline; the honest
  // status is "queued · job #<id>", not a completion summary.
  const detail = rerun.error
    ? formatApiError(rerun.error)
    : rerun.data
      ? `queued · job #${rerun.data.job_id}`
      : null;

  return (
    <>
      <button
        type="button"
        className={styles.rerunChip}
        onClick={() => setOpen(true)}
        disabled={rerun.isPending}
        title="Run the aggregator again for this candidate"
      >
        {rerun.isPending ? 'Queuing…' : 'Re-run aggregator'}
      </button>
      {detail && !open && (
        <span className={rerun.error ? styles.rerunStatusError : styles.rerunStatus}>
          {detail}
        </span>
      )}
      {open && (
        <div
          className={styles.cohortBackdrop}
          onClick={(e) => {
            if (e.target === e.currentTarget && !rerun.isPending) setOpen(false);
          }}
        >
          <section className={styles.rerunPanel}>
            <header className={styles.cohortHeader}>
              <h3 className={styles.cohortTitle}>Re-run aggregator</h3>
              <button
                type="button"
                className={styles.cohortCloseBtn}
                onClick={() => setOpen(false)}
                disabled={rerun.isPending}
              >
                ×
              </button>
            </header>
            <div className={styles.rerunBody}>
              <div className={styles.rerunMeta}>
                candidate {dealKey}
              </div>
              <textarea
                className={styles.rerunTextarea}
                value={reason}
                maxLength={500}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Optional audit note"
                disabled={rerun.isPending}
              />
              {rerun.data && (
                <div className={styles.rerunResult}>
                  <div>queued · job #{rerun.data.job_id}</div>
                  <div className={styles.rerunMeta}>
                    Queued — the scheduler process runs it; watch Admin → Maintenance jobs.
                  </div>
                </div>
              )}
              {rerun.error && (
                <div className={styles.errorBox}>
                  Failed: {formatApiError(rerun.error)}
                </div>
              )}
              <div className={styles.rerunActions}>
                <button
                  type="button"
                  className={styles.clearBtn}
                  onClick={() => setOpen(false)}
                  disabled={rerun.isPending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={styles.rerunPrimaryBtn}
                  disabled={rerun.isPending}
                  onClick={() => {
                    rerun.mutate(
                      { dealKey, reason },
                      {
                        onSuccess: () => {
                          setReason('');
                        },
                      },
                    );
                  }}
                >
                  {rerun.isPending ? 'Queuing…' : 'Run now'}
                </button>
              </div>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

function ObjectView({
  view, timelineLabel, timeline, titleAction,
}: {
  view: { kind: string; summary: string; stepper: StepperStage[] };
  timelineLabel: string;
  timeline: ReactNode;
  titleAction?: ReactNode;
}) {
  return (
    <div className={styles.objectView}>
      <div className={styles.objectTitleRow}>
        <h2 className={styles.objectTitle}>{timelineLabel}</h2>
        {titleAction}
      </div>

      <section className={styles.summaryBox}>
        <div className={styles.summaryLabel}>Summary</div>
        <p className={styles.summaryText}>{view.summary}</p>
      </section>

      <Stepper stages={view.stepper} />

      <details className={styles.timelineDetails} open>
        <summary className={styles.timelineSummary}>
          Detailed timeline
        </summary>
        <div className={styles.timelineBody}>{timeline}</div>
      </details>
    </div>
  );
}

function Stepper({ stages }: { stages: StepperStage[] }) {
  if (!stages || stages.length === 0) return null;
  return (
    <section className={styles.stepper}>
      <div className={styles.stepperLabel}>Pipeline</div>
      <ol className={styles.stepperList}>
        {stages.map((s, i) => (
          <li
            key={`${s.name}-${i}`}
            className={`${styles.stepperStage} ${styles[`status_${s.status}`] || ''}`}
            title={s.detail || s.status}
          >
            <span className={styles.stageIcon}>
              {s.status === 'passed' && '✓'}
              {s.status === 'failed' && '✗'}
              {s.status === 'changed' && '⚠'}
              {s.status === 'refuted' && '🛇'}
              {s.status === 'pending' && '⏸'}
              {s.status === 'skipped' && '–'}
            </span>
            <span className={styles.stageName}>{s.name}</span>
            {s.detail && (
              <span className={styles.stageDetail}>{s.detail}</span>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

function formatApiError(err: unknown): string {
  const maybe = err as {
    message?: string;
    detail?: unknown;
  };
  const detail = maybe.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    const nested = detail as { detail?: unknown };
    if (typeof nested.detail === 'string') return nested.detail;
  }
  return maybe.message || String(err);
}

interface CohortPickerState {
  cohort: string;
  params: Record<string, string | number | undefined>;
  title: string;
}

function HealthMode({
  onJumpToSignal,
  onJumpToDeal,
}: {
  onJumpToSignal: (signalId: number) => void;
  onJumpToDeal: (kind: 'candidate' | 'affinity_opp', key: string) => void;
}) {
  const q = useHealthIssues();
  // PR 6 — When the deal-card "📈 Show similar overrides" chip
  // deep-links us here with ?cohort=field_override&field_name=...,
  // auto-open the cohort modal so the operator lands on the matching
  // queue rather than scanning issue rows. Read from the LIVE URL at mount
  // (not a page-level memo): HealthMode unmounts/remounts on mode switches and
  // the modal strips cohort params on close, so re-reading here means a remount
  // after dismissing the modal won't reopen it (review F4).
  const [cohort, setCohortRaw] = useState<CohortPickerState | null>(() => {
    const ic = readInitialCohort();
    if (!ic) return null;
    return {
      cohort: ic.cohort,
      params: { field_name: ic.fieldName, days: ic.days },
      title: ic.fieldName ? `${ic.cohort} · ${ic.fieldName}` : ic.cohort,
    };
  });
  // Mirror the open cohort into the URL (the same cohort/field_name/days that
  // readInitialCohort reads back) so an in-app cohort open is reload-stable +
  // shareable, matching the deep-link-in case (spec §5.3). `mode=health` is
  // already in the URL while Health mode is active. Replace, not push.
  const setCohort = useCallback((next: CohortPickerState | null) => {
    setCohortRaw(next);
    const url = new URL(window.location.href);
    url.searchParams.delete('cohort');
    url.searchParams.delete('field_name');
    url.searchParams.delete('days');
    if (next) {
      url.searchParams.set('cohort', next.cohort);
      const fn = next.params?.field_name;
      const days = next.params?.days;
      if (fn != null && fn !== '') url.searchParams.set('field_name', String(fn));
      if (days != null && days !== '') url.searchParams.set('days', String(days));
    }
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  }, []);

  if (q.isLoading) return <div className={styles.empty}>Loading health…</div>;
  if (q.error) {
    return (
      <div className={styles.errorBox}>
        Failed: {(q.error as Error).message}
      </div>
    );
  }
  if (!q.data) return null;

  const redIssues = q.data.issues.filter((i) => i.severity === 'red');
  const amberIssues = q.data.issues.filter((i) => i.severity === 'amber');
  const okIssues = q.data.issues.filter((i) => i.severity === 'ok');

  return (
    <div className={styles.health}>
      <section>
        <h2 className={styles.sectionTitle}>Today's issues</h2>
        {redIssues.length === 0 && amberIssues.length === 0 && (
          <div className={styles.allGreen}>
            ✓ All calibration metrics in range. No action needed.
          </div>
        )}
        {[...redIssues, ...amberIssues].map((issue) => (
          <IssueRow
            key={`${issue.kind}-${issue.title}`}
            issue={issue}
            onAction={(action) => {
              if (action.action === 'view_cohort' && action.cohort) {
                setCohort({
                  cohort: action.cohort,
                  params: (action.params || {}) as Record<string, string | number | undefined>,
                  title: action.label || issue.title,
                });
              } else if (action.action === 'open_prompts') {
                // PR 4 — open Prompts tab Live Preview with impact-context
                // strip. Deep-link encodes the source issue + purpose so the
                // operator lands with context loaded.
                const params = new URLSearchParams();
                params.set('purpose', action.purpose || 'aggregator');
                params.set('from_pi', issue.kind);
                if (action.params) {
                  Object.entries(action.params).forEach(([k, v]) => {
                    params.set(k, String(v));
                  });
                }
                window.location.href = `/?tab=prompts&${params.toString()}`;
              } else if (action.action === 'filter_ledger') {
                // PR 7 retired the Engine tab. Until Section D of Health
                // mode lands (spec §6.2.4), the cohort modal is the
                // best stand-in for ledger filtering — open it.
                if (action.params) {
                  setCohort({
                    cohort: (action.params.cohort as string) || 'field_override',
                    params: (action.params || {}) as Record<string, string | number | undefined>,
                    title: action.label || issue.title,
                  });
                }
              }
            }}
          />
        ))}
      </section>

      {okIssues.length > 0 && (
        <section className={styles.okSection}>
          <h3 className={styles.sectionSub}>In range</h3>
          {okIssues.map((issue) => (
            <div
              key={`${issue.kind}-${issue.title}`}
              className={styles.okIssue}
            >
              <span>✓ {issue.title}</span>
              <span className={styles.okIssueDetail}>{issue.detail}</span>
            </div>
          ))}
        </section>
      )}

      {cohort && (
        <CohortModal
          state={cohort}
          onClose={() => setCohort(null)}
          onJumpToSignal={(id) => {
            setCohort(null);
            onJumpToSignal(id);
          }}
          onJumpToDeal={(k, key) => {
            setCohort(null);
            onJumpToDeal(k, key);
          }}
        />
      )}
    </div>
  );
}

function IssueRow({
  issue, onAction,
}: {
  issue: HealthIssue;
  onAction: (a: IssueAction) => void;
}) {
  const dot = (
    <span className={`${styles.issueDot} ${styles[`dot_${issue.severity}`]}`}>
      {issue.severity === 'red' ? '🔴' : issue.severity === 'amber' ? '🟡' : '✓'}
    </span>
  );
  return (
    <div className={`${styles.issueRow} ${styles[`issueRow_${issue.severity}`]}`}>
      <div className={styles.issueHeader}>
        {dot}
        <span className={styles.issueTitle}>{issue.title}</span>
      </div>
      <div className={styles.issueDetail}>{issue.detail}</div>
      {issue.actions.length > 0 && (
        <div className={styles.issueActions}>
          {issue.actions.map((a, i) => (
            <button
              key={i}
              type="button"
              className={styles.actionChip}
              onClick={() => onAction(a)}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CohortModal({
  state, onClose, onJumpToSignal, onJumpToDeal,
}: {
  state: CohortPickerState;
  onClose: () => void;
  onJumpToSignal: (id: number) => void;
  onJumpToDeal: (k: 'candidate' | 'affinity_opp', key: string) => void;
}) {
  const q = useCohort(state.cohort, state.params);
  return (
    <div
      className={styles.cohortBackdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.cohortPanel}>
        <header className={styles.cohortHeader}>
          <h3 className={styles.cohortTitle}>{state.title}</h3>
          <button
            type="button"
            className={styles.cohortCloseBtn}
            onClick={onClose}
          >
            ×
          </button>
        </header>
        {q.isLoading && <div className={styles.empty}>Loading…</div>}
        {q.error && (
          <div className={styles.errorBox}>
            Failed: {(q.error as Error).message}
          </div>
        )}
        {q.data && q.data.items.length === 0 && (
          <div className={styles.empty}>
            (no recent examples in this cohort)
          </div>
        )}
        {q.data && q.data.items.length > 0 && (
          <ul className={styles.cohortList}>
            {q.data.items.map((item, i) => (
              <CohortItemRow
                key={i}
                item={item}
                onJumpToSignal={onJumpToSignal}
                onJumpToDeal={onJumpToDeal}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function CohortItemRow({
  item, onJumpToSignal, onJumpToDeal,
}: {
  item: CohortItem;
  onJumpToSignal: (id: number) => void;
  onJumpToDeal: (k: 'candidate' | 'affinity_opp', key: string) => void;
}) {
  const v = item.view_in_pipeline_insights;
  const jump = () => {
    if (!v) return;
    if (v.kind === 'signal' && v.signal_id !== undefined) {
      onJumpToSignal(v.signal_id);
    } else if (v.kind === 'deal' && v.deal_kind && v.deal_key) {
      onJumpToDeal(v.deal_kind, v.deal_key);
    }
  };

  if (item.kind === 'signal') {
    return (
      <li className={styles.cohortItem}>
        <button type="button" className={styles.cohortItemBtn} onClick={jump}>
          <div className={styles.cohortItemTitle}>
            <span className={styles.cohortKindTag}>signal #{item.signal_id}</span>
            {item.sender && (
              <span className={styles.cohortItemSender}>{item.sender}</span>
            )}
          </div>
          <div className={styles.cohortItemDetail}>
            {item.subject}
            {item.signal_type && (
              <span className={styles.cohortItemMeta}> · {item.signal_type}</span>
            )}
            {item.extracted_name && (
              <span className={styles.cohortItemMeta}> · {item.extracted_name}</span>
            )}
          </div>
          {item.classifier_reason && (
            <div className={styles.cohortItemReason}>
              {item.classifier_reason}
            </div>
          )}
        </button>
      </li>
    );
  }
  if (item.kind === 'field_edit') {
    return (
      <li className={styles.cohortItem}>
        <button type="button" className={styles.cohortItemBtn} onClick={jump}>
          <div className={styles.cohortItemTitle}>
            <span className={styles.cohortKindTag}>{item.field_name}</span>
            <span className={styles.cohortItemSender}>
              {item.deal_kind === 'candidate' ? 'candidate' : `opp ${item.deal_key}`}
            </span>
          </div>
          <div className={styles.cohortItemDetail}>
            proposed: <code>{JSON.stringify(item.proposed_value)}</code> →
            {' '}user: <code>{JSON.stringify(item.user_value)}</code>
          </div>
          <div className={styles.cohortItemMeta}>
            {item.proposed_confidence} confidence · edited by {item.edited_by}
          </div>
        </button>
      </li>
    );
  }
  // deal cohort (frequent unbinds)
  return (
    <li className={styles.cohortItem}>
      <button type="button" className={styles.cohortItemBtn} onClick={jump}>
        <div className={styles.cohortItemTitle}>
          <span className={styles.cohortKindTag}>
            {item.deal_kind === 'candidate' ? 'candidate' : 'opp'}{' '}
            {item.deal_key}
          </span>
          <span className={styles.cohortItemSender}>
            {item.refute_count} refutes
          </span>
        </div>
      </button>
    </li>
  );
}


// ── Events mode (multi-pipeline observability slice 2) ──────────────────────

function EventsMode({ onJumpToSignal }: { onJumpToSignal: (signalId: number) => void }) {
  // Events filters live in the URL (replace — reload-stable + shareable, not a
  // Back-step). Empty pipeline = "All"; unresolved is '1' when on.
  const [pipelineParam, setPipelineParam] = useViewParam('pipeline', '', { history: 'replace' });
  const [unresolvedParam, setUnresolvedParam] = useViewParam('unresolved', '', { history: 'replace' });
  const pipeline = pipelineParam || null;
  const unresolved = unresolvedParam === '1';
  const { data, isLoading, error } = usePipelineEvents({ pipeline, unresolved, sinceDays: 30 });

  const signalIdOf = (eventSourceId: string | null): number | null => {
    if (!eventSourceId || !eventSourceId.startsWith('signal:')) return null;
    const n = Number(eventSourceId.slice('signal:'.length));
    return Number.isFinite(n) ? n : null;
  };

  return (
    <section style={{ padding: '12px 0' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <button
          type="button"
          className={`${styles.segBtn} ${pipeline === null ? styles.segBtnActive : ''}`}
          onClick={() => setPipelineParam('')}
        >
          All
        </button>
        {(data?.facets ?? []).map((f) => (
          <button
            key={f.pipeline}
            type="button"
            className={`${styles.segBtn} ${pipeline === f.pipeline ? styles.segBtnActive : ''}`}
            onClick={() => setPipelineParam(f.pipeline)}
          >
            {f.label} ({f.count})
          </button>
        ))}
        <label style={{ marginLeft: 'auto', fontSize: 13, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={unresolved} onChange={(e) => setUnresolvedParam(e.target.checked ? '1' : '')} />
          Unresolved only
        </label>
      </div>

      {isLoading && <p>Loading…</p>}
      {error && <p style={{ color: '#c0392b' }}>{(error as Error).message}</p>}
      {data && data.events.length === 0 && <p style={{ color: '#888' }}>No events match.</p>}

      {data && data.events.length > 0 && (
        <div className={styles.eventsTableWrap}>
        <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%', minWidth: 560 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
              <th style={{ padding: '6px 8px' }}>Pipeline</th>
              <th style={{ padding: '6px 8px' }}>Event</th>
              <th style={{ padding: '6px 8px' }}>When</th>
              <th style={{ padding: '6px 8px' }}>State / artifact</th>
            </tr>
          </thead>
          <tbody>
            {data.events.map((e) => {
              const sid = signalIdOf(e.event_source_id);
              const c = e.context;
              const ctxLine = c
                ? [
                    [c.subject, c.sender].filter(Boolean).join(' — '),
                    c.verdict ? `${c.verdict}${c.confidence ? ` (${c.confidence})` : ''}` : '',
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : '';
              return (
                <tr key={e.event_log_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '6px 8px', fontFamily: 'DM Mono, monospace' }}>{e.pipeline}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <div style={{ fontFamily: 'DM Mono, monospace' }}>
                      {sid != null ? (
                        <button
                          onClick={() => onJumpToSignal(sid)}
                          style={{ background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer', padding: 0 }}
                        >
                          {e.event_type} · #{e.event_log_id}
                        </button>
                      ) : (
                        <>{e.event_type} · #{e.event_log_id}</>
                      )}
                    </div>
                    {ctxLine && (
                      <div
                        title={ctxLine}
                        style={{
                          fontSize: 11, color: '#888', marginTop: 2, maxWidth: 440,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {ctxLine}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '6px 8px', color: '#888' }}>
                    {e.occurred_at ? e.occurred_at.slice(0, 16).replace('T', ' ') : '—'}
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    {e.unresolved_state ? (
                      <span style={{ color: e.unresolved_state === 'stuck' ? '#c0392b' : '#b7791f', fontWeight: 600 }}>
                        {e.unresolved_state.replace(/_/g, ' ')}
                      </span>
                    ) : e.deal_label && e.deal_link ? (
                      <>
                        <a href={e.deal_link} style={{ color: '#0E7C66' }}>{e.deal_label}</a>
                        {e.artifact && (
                          <a href={e.artifact.deep_link} style={{ color: '#888', fontSize: 11, marginLeft: 6 }}>
                            · {e.artifact.kind}
                          </a>
                        )}
                      </>
                    ) : e.artifact ? (
                      <a href={e.artifact.deep_link} style={{ color: '#0E7C66' }}>
                        {e.artifact.label ?? e.artifact.kind}
                        {e.artifact.status ? ` · ${e.artifact.status}` : ''}
                      </a>
                    ) : (
                      <span style={{ color: '#888' }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
      {data?.has_more && <p style={{ color: '#888', fontSize: 12, marginTop: 8 }}>Showing first {data.limit}.</p>}
    </section>
  );
}
